import { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Stack,
    Typography,
    CircularProgress,
} from "@mui/material";

const TYPES = [
    { label: "Marketing", value: "MARKETING" },
    { label: "Cleaning", value: "CLEANING" },
    { label: "IT", value: "IT" },
    { label: "Perfume", value: "PERFUME" },
    { label: "Security", value: "SECURITY" },
    { label: "Other", value: "OTHER" },
];

const PRICING = [
    { label: "Monthly", value: "MONTHLY" },
    { label: "Fixed", value: "FIXED" },
];

export default function VendorFormDialog({
    open,
    onClose,
    onSubmit,
    centers,
    editRow,
    saving = false,
    uploading = false,
    uploadResult = "idle", // idle | ok | fail
}) {
    const [form, setForm] = useState({
        center: "",
        name: "",
        type: "OTHER",
        phone: "",
        email: "",
        notes: "",
        contract: {
            pricingType: "MONTHLY",
            amount: 0,
            currency: "AED",
            startDate: "",
            endDate: "",
            status: "ACTIVE",
            file: null,
        },
    });

    useEffect(() => {
        if (editRow) {
            setForm({
                center: editRow.center?._id || editRow.center || "",
                name: editRow.name || "",
                type: editRow.type || "OTHER",
                phone: editRow.phone || "",
                email: editRow.email || "",
                notes: editRow.notes || "",
                contract: {
                    pricingType: editRow.contract?.pricingType || "MONTHLY",
                    amount: editRow.contract?.amount ?? 0,
                    currency: editRow.contract?.currency || "AED",
                    startDate: editRow.contract?.startDate
                        ? editRow.contract.startDate.slice(0, 10)
                        : "",
                    endDate: editRow.contract?.endDate
                        ? editRow.contract.endDate.slice(0, 10)
                        : "",
                    status: editRow.contract?.status || "ACTIVE",
                    file: null, // ما منجيب الملف من DB
                },
            });
        } else {
            // reset عند إضافة جديدة
            setForm({
                center: "",
                name: "",
                type: "OTHER",
                phone: "",
                email: "",
                notes: "",
                contract: {
                    pricingType: "MONTHLY",
                    amount: 0,
                    currency: "AED",
                    startDate: "",
                    endDate: "",
                    status: "ACTIVE",
                    file: null,
                },
            });
        }
    }, [editRow, open]);

    const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));
    const setContract = (key, value) =>
        setForm((p) => ({ ...p, contract: { ...p.contract, [key]: value } }));

    const handleSave = () => {
        if (!form.center) return alert("Center is required");
        if (!form.name) return alert("Name is required");
        if (!form.contract.startDate) return alert("Start date is required");

        onSubmit(form);
    };

    const fileName = form.contract.file?.name || "";

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>{editRow ? "Edit Vendor" : "Add Vendor"}</DialogTitle>

            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        select
                        label="Center"
                        value={form.center}
                        onChange={(e) => setField("center", e.target.value)}
                        fullWidth
                        disabled={saving}
                    >
                        {centers.map((c) => (
                            <MenuItem key={c._id} value={c._id}>
                                {c.name} ({c.code})
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label="Company Name"
                        value={form.name}
                        onChange={(e) => setField("name", e.target.value)}
                        fullWidth
                        disabled={saving}
                    />

                    <TextField
                        select
                        label="Type"
                        value={form.type}
                        onChange={(e) => setField("type", e.target.value)}
                        fullWidth
                        disabled={saving}
                    >
                        {TYPES.map((t) => (
                            <MenuItem key={t.value} value={t.value}>
                                {t.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Phone"
                            value={form.phone}
                            onChange={(e) => setField("phone", e.target.value)}
                            fullWidth
                            disabled={saving}
                        />
                        <TextField
                            label="Email"
                            value={form.email}
                            onChange={(e) => setField("email", e.target.value)}
                            fullWidth
                            disabled={saving}
                        />
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            select
                            label="Pricing"
                            value={form.contract.pricingType}
                            onChange={(e) => setContract("pricingType", e.target.value)}
                            fullWidth
                            disabled={saving}
                        >
                            {PRICING.map((p) => (
                                <MenuItem key={p.value} value={p.value}>
                                    {p.label}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label="Amount"
                            type="number"
                            value={form.contract.amount}
                            onChange={(e) => setContract("amount", Number(e.target.value))}
                            fullWidth
                            disabled={saving}
                        />
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Start Date"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={form.contract.startDate}
                            onChange={(e) => setContract("startDate", e.target.value)}
                            fullWidth
                            disabled={saving}
                        />
                        <TextField
                            label="End Date"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={form.contract.endDate}
                            onChange={(e) => setContract("endDate", e.target.value)}
                            fullWidth
                            disabled={saving}
                        />
                    </Stack>

                    <TextField
                        select
                        label="Contract Status"
                        value={form.contract.status}
                        onChange={(e) => setContract("status", e.target.value)}
                        fullWidth
                        disabled={saving}
                    >
                        <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                        <MenuItem value="EXPIRED">EXPIRED</MenuItem>
                        <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
                    </TextField>

                    {/* Upload */}
                    <Stack spacing={1}>
                        <Button variant="outlined" component="label" disabled={saving}>
                            Upload Contract File
                            <input
                                hidden
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg"
                                onChange={(e) => setContract("file", e.target.files?.[0] || null)}
                            />
                        </Button>

                        {fileName ? (
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                الملف المختار: <b>{fileName}</b>
                            </Typography>
                        ) : (
                            <Typography variant="body2" sx={{ opacity: 0.6 }}>
                                لم يتم اختيار ملف
                            </Typography>
                        )}

                        {uploading && (
                            <Typography variant="body2">
                                عم يتم رفع الملف...
                            </Typography>
                        )}

                        {!uploading && uploadResult === "ok" && (
                            <Typography variant="body2" color="success.main">
                                تم رفع الملف ✅
                            </Typography>
                        )}

                        {!uploading && uploadResult === "fail" && (
                            <Typography variant="body2" color="error.main">
                                فشل رفع الملف ❌
                            </Typography>
                        )}
                    </Stack>

                    <TextField
                        label="Notes"
                        value={form.notes}
                        onChange={(e) => setField("notes", e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        disabled={saving}
                    />
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>

                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CircularProgress size={18} />
                            <span>Saving...</span>
                        </Stack>
                    ) : (
                        "Save"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
